/**
 * JS-accessible mirrors of the CSS variables in src/styles/globals.css.
 * Use these when you need a token value inside a JS context (e.g. inline
 * style, canvas drawing) — for normal styling reference the CSS variables
 * directly so consumers stay consistent.
 */

export const RAMP_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
export type RampStep = (typeof RAMP_STEPS)[number];

export type Ramp = Record<RampStep, string>;

/**
 * Nine nine-step ramps. Brand chips sit at fixed steps:
 *   warms.200 = Beige, warms.300 = Overlay, warms.500 = Tope
 *   navy.700    = Navy
 *   orange.700  = Sunshine
 *   Lavender    = #BC75D3 (its own value, not a ramp step — between
 *                 purple.700 #9F6FBE and the Figma lavender #CF87E6)
 *   neutral.100 = Drawer, neutral.700 = Grey
 *   red.500     = #F53646
 *   green.500   = #40C152
 */
export const ramps = {
  neutral: {
    100: "#FCFCFA",
    200: "#EDEBE3",
    300: "#D6D2C8",
    400: "#ADA89B",
    500: "#847F72",
    600: "#615C50",
    700: "#4A4640",
    800: "#2D2A25",
    900: "#161512",
  },
  warms: {
    100: "#FAF9F4",
    200: "#F3EEDE",
    300: "#E5D7BA",
    400: "#D4C5A8",
    500: "#C7B8A0",
    600: "#9E8E76",
    700: "#6E6353",
    800: "#463E32",
    900: "#2A241B",
  },
  navy: {
    100: "#DDE3F5",
    200: "#BFC8EA",
    300: "#929CD4",
    400: "#5C66B2",
    500: "#3640A2",
    600: "#1D2486",
    700: "#13166C",
    800: "#0A0C47",
    900: "#06082E",
  },
  orange: {
    100: "#FDEFD0",
    200: "#FBDBA0",
    300: "#F8C474",
    400: "#F4B354",
    500: "#F2AC48",
    600: "#F1AA42",
    700: "#F1A83E",
    800: "#B85F18",
    900: "#823210",
  },
  orangeProposed: {
    100: "#FFE9C2",
    200: "#FECF85",
    300: "#FDB454",
    400: "#FB9F30",
    500: "#F9911A",
    600: "#F18610",
    700: "#E37707",
    800: "#B85B00",
    900: "#823F00",
  },
  purple: {
    100: "#FCF1FA",
    200: "#ECD4EC",
    300: "#D6B5DC",
    400: "#B98DC5",
    500: "#AB7DBE",
    600: "#A573BB",
    700: "#9F6FBE",
    800: "#82339F",
    900: "#61168A",
  },
  green: {
    100: "#EAF1D2",
    200: "#CFE9C0",
    300: "#98D599",
    400: "#5DCB73",
    500: "#40C152",
    600: "#29A73D",
    700: "#1C8F33",
    800: "#157A2A",
    900: "#0D6321",
  },
  red: {
    100: "#FCE0DA",
    200: "#FACBC0",
    300: "#F4A099",
    400: "#F37580",
    500: "#F53646",
    600: "#DD2238",
    700: "#BD1A2D",
    800: "#9D1424",
    900: "#7A0E1B",
  },
  yellow: {
    100: "#FFF9C9",
    200: "#FFF089",
    300: "#FFE74F",
    400: "#FFDB1F",
    500: "#FFD000",
    600: "#FAC300",
    700: "#F4B500",
    800: "#D29A00",
    900: "#A47600",
  },
  cyan: {
    100: "#D4F2FB",
    200: "#A5E3F4",
    300: "#69CCE7",
    400: "#2DB3D7",
    500: "#1AA0CC",
    600: "#128AB0",
    700: "#0A7494",
    800: "#075E78",
    900: "#044354",
  },
} as const satisfies Record<string, Ramp>;

export type RampName = keyof typeof ramps;

/**
 * Named brand chips. Each is a labeled position inside one of the ramps.
 * `colors` stays as the flat, app-facing set of hexes.
 */
export const colors = {
  // Surfaces.
  app: "#FAF6F0",
  drawer: ramps.neutral[100],
  beige: ramps.warms[200],
  beigeOverlay: ramps.warms[300],

  // Brand.
  navy: ramps.navy[700],
  sunshine: ramps.orange[700],
  lavender: "#BC75D3",
  tope: ramps.warms[500],
  grey: ramps.neutral[700],

  // Status / semantic.
  positive: ramps.green[500],
  negative: ramps.red[500],
} as const;

export type ColorToken = keyof typeof colors;

export const motion = {
  ease: {
    cubicOut: "cubic-bezier(0.215, 0.610, 0.355, 1.000)",
    cubicInOut: "cubic-bezier(0.645, 0.045, 0.355, 1.000)",
  },
  duration: {
    fast: 150,
    medium: 250,
    slow: 400,
  },
} as const;

/** Brand-palette card colors. Selected by users in the kanban. */
export const CARD_COLORS = ["navy", "sunshine", "lavender", "tope"] as const;
export type CardColor = (typeof CARD_COLORS)[number];

export const CARD_COLOR_LABELS: Record<CardColor, string> = {
  navy: "Navy",
  sunshine: "Sunshine",
  lavender: "Lavender",
  tope: "Tope",
};

export const CARD_COLOR_VAR: Record<CardColor, string> = {
  navy: "var(--color-navy)",
  sunshine: "var(--color-sunshine)",
  lavender: "var(--color-lavender)",
  tope: "var(--color-tope)",
};
